import Form from 'antd/lib/form';
import FormItem from 'antd/lib/form/FormItem';
import useForm from 'antd/lib/form/hooks/useForm';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import * as React from 'react';
import { getIsGooglePlayStore, getIsAmazonAppStore, isGooglePlatform } from '@/helpers';
import { AppPlatform, AppStoreAvailableBuild, AppStoreType } from '@/types';
import { AppBuildComponent, CIBuildComponent } from '../BuildComponents';
import LinkageIcon from '../icons/LinkageIcon';
import Text from 'antd/lib/typography/Text';
import { displayConsoleNameForAppStoreType } from '@/helpers/displayHelpers';
import { ReleaseComponentProps } from '@/typescriptHelpers';
import { forEach, reduce } from 'lodash';
import Tooltip from 'antd/lib/tooltip';
import { antGrey } from '../styles/globalStyles';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { dayjs } from '@/extendedDayjs';
import { getLocaleDateTimeFormat } from '@/timeHelpers';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { selectAppTimeFormatSetting } from '@/selectors/selectors';

interface ChangeSelectedBuildModalBaseProps extends ReleaseComponentProps {
    platform: AppPlatform;
    appStoreType: AppStoreType;
    title: string;
    label: string;
    isLoading: boolean;
    includeAssociatedCIBuilds: boolean;
    showRecentBuilds: boolean;
    showPastBuildsEmptyState: boolean;
    open: boolean;
    currentSelection?: AppStoreAvailableBuild[];
    availableBuilds: AppStoreAvailableBuild[];
    onSaveChanges: (buildIds: string[]) => void;
    onClose: () => void;
    onSuccess: () => void;
    showsClearButton?: boolean;
    footer?: React.ReactNode;
}

const ChangeSelectedBuildModalBase: React.FC<ChangeSelectedBuildModalBaseProps> = (
    props: ChangeSelectedBuildModalBaseProps,
) => {
    const [form] = useForm();
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));

    const isGooglePlay = getIsGooglePlayStore(props.appStoreType);
    const selectsMultiple = isGooglePlay;

    const onSaveChanges = () => {
        form.validateFields().then((values) => {
            let buildIds = null;

            if (typeof values.selectedAppStoreBuilds === 'string') {
                buildIds = [values.selectedAppStoreBuilds];
            } else {
                buildIds = values.selectedAppStoreBuilds;
            }

            props.onSaveChanges(buildIds);
        });
    };

    const extraBuildInfo = (appStoreBuild: AppStoreAvailableBuild) => {
        if (appStoreBuild.isCurrentlySelected && appStoreBuild.isNewestAppStoreBuild) {
            return <Text>(latest, selected)</Text>;
        } else if (appStoreBuild.isCurrentlySelected) {
            return <Text>(currently selected)</Text>;
        } else if (appStoreBuild.isNewestAppStoreBuild) {
            return <Text>(latest)</Text>;
        }

        return null;
    };

    const renderAvailableBuild = (
        availableBuild: AppStoreAvailableBuild,
        includeAssociatedCIBuild: boolean,
        forceDisabled: boolean,
    ) => {
        const baseBuildInfo = (
            <Space>
                <AppBuildComponent
                    integrationId={availableBuild.appStoreBuild.integrationId}
                    buildIdentifier={availableBuild.appStoreBuild.buildNumber}
                    isProcessed={availableBuild.appStoreBuild.isProcessed}
                />
                {includeAssociatedCIBuild && (
                    <>
                        <LinkageIcon width={16} height={16} />
                        {availableBuild.associatedCIBuild && (
                            <CIBuildComponent
                                integrationId={availableBuild.associatedCIBuild.integrationId}
                                buildIdentifier={availableBuild.associatedCIBuild.buildIdentifier}
                            />
                        )}
                        {!availableBuild.associatedCIBuild && <Text type="secondary">Unknown</Text>}
                        {extraBuildInfo(availableBuild)}
                        {availableBuild.associatedCIBuild?.commitMessage && (
                            <Text type="secondary" ellipsis>
                                - {availableBuild.associatedCIBuild.commitMessage}
                            </Text>
                        )}
                    </>
                )}
            </Space>
        );

        const optionDisabled = forceDisabled || !availableBuild.appStoreBuild.isProcessed;

        const uploadTimestamp =
            isGooglePlay && availableBuild.appStoreBuild.uploadedAt ?
                dayjs(availableBuild.appStoreBuild.uploadedAt).format(
                    getLocaleDateTimeFormat('short', use24HourTimeFormat),
                )
            :   null;

        return (
            <Select.Option
                key={availableBuild.appStoreBuild.id}
                value={availableBuild.appStoreBuild.id}
                disabled={optionDisabled}
                label={baseBuildInfo}
            >
                <Space direction="vertical" size={0} style={{ opacity: optionDisabled ? 0.5 : 1.0 }}>
                    {uploadTimestamp ?
                        <Tooltip title={`Uploaded ${uploadTimestamp}`}>{baseBuildInfo}</Tooltip>
                    :   baseBuildInfo}
                    {availableBuild.tracks && availableBuild.tracks.length > 0 && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            - Found on tracks: {availableBuild.tracks.join(', ')}
                        </Text>
                    )}
                </Space>
            </Select.Option>
        );
    };

    React.useEffect(() => {
        if (props.open) {
            if (props.currentSelection) {
                const atLeastOneSelectedBuildAvailableInList = reduce(
                    props.currentSelection,
                    (buildAvailableInList, build) => {
                        if (buildAvailableInList) {
                            return true;
                        } else {
                            const buildInList = props.availableBuilds.find(
                                (availableBuild) => availableBuild.appStoreBuild.id === build.appStoreBuild.id,
                            );

                            return !!buildInList;
                        }
                    },
                    false,
                );

                if (atLeastOneSelectedBuildAvailableInList) {
                    if (props.currentSelection.length > 1) {
                        const buildIds = props.currentSelection.map((build) => build.appStoreBuild.id);
                        form.setFieldsValue({
                            selectedAppStoreBuilds: buildIds,
                        });
                    } else {
                        const selectedBuild = props.currentSelection[0];
                        form.setFieldsValue({
                            selectedAppStoreBuilds:
                                selectsMultiple ? [selectedBuild.appStoreBuild.id] : selectedBuild.appStoreBuild.id,
                        });
                    }
                } else {
                    form.setFieldsValue({ selectedAppStoreBuilds: selectsMultiple ? [] : null });
                }
            } else {
                form.setFieldsValue({ selectedAppStoreBuilds: selectsMultiple ? [] : null });
            }
        }
    }, [props.open]);

    const recentBuilds = [];
    const previousBuilds = [];

    forEach(props.availableBuilds, (build: AppStoreAvailableBuild) => {
        if (build.isRecentBuild) {
            recentBuilds.push(build);
        } else {
            previousBuilds.push(build);
        }
    });

    const isAmazonAppStore = getIsAmazonAppStore(props.appStoreType);

    // Past builds for Amazon are the builds fetched from the Amazon Appstore, and we can't actually interact with them
    const shouldDisablePastBuildSelection = isAmazonAppStore;

    return (
        <Modal
            title={props.title}
            onOk={() => form.submit()}
            okButtonProps={{ loading: props.isLoading }}
            onCancel={props.onClose}
            destroyOnClose
            open={props.open}
            okText="Save changes"
            cancelText="Discard changes"
            afterClose={() => form.resetFields()}
            width={600}
        >
            <Form form={form} layout="vertical" onFinish={onSaveChanges}>
                <FormItem
                    label={
                        <Space>
                            <Text>{props.label}</Text>
                        </Space>
                    }
                    name="selectedAppStoreBuilds"
                    required
                >
                    <Select
                        allowClear={props.showsClearButton}
                        placeholder="Select"
                        mode={selectsMultiple ? 'multiple' : null}
                        optionLabelProp="label"
                    >
                        {props.showRecentBuilds && (
                            <Select.OptGroup label={buildsLabelForPlatform(props.platform, true, props.appStoreType)}>
                                {recentBuilds.map((build) =>
                                    renderAvailableBuild(build, props.includeAssociatedCIBuilds, false),
                                )}
                                {recentBuilds.length === 0 && (
                                    <Select.Option disabled>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            No recent builds
                                        </Text>
                                    </Select.Option>
                                )}
                            </Select.OptGroup>
                        )}
                        {(props.showPastBuildsEmptyState || previousBuilds.length !== 0) && (
                            <Select.OptGroup label={buildsLabelForPlatform(props.platform, false, props.appStoreType)}>
                                {previousBuilds.map((build) =>
                                    renderAvailableBuild(
                                        build,
                                        props.includeAssociatedCIBuilds,
                                        shouldDisablePastBuildSelection,
                                    ),
                                )}
                                {props.showPastBuildsEmptyState && previousBuilds.length === 0 && (
                                    <Select.Option disabled>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            No recent builds
                                        </Text>
                                    </Select.Option>
                                )}
                            </Select.OptGroup>
                        )}
                    </Select>
                </FormItem>
                {props.footer}
            </Form>
        </Modal>
    );
};

export default ChangeSelectedBuildModalBase;

const buildsLabelForPlatform = (platform: AppPlatform, isRecent: boolean, appStoreType?: AppStoreType) => {
    let tooltipText = null;
    let labelText = isRecent ? 'Recent builds' : 'Past builds';

    if (isGooglePlatform(platform)) {
        const isAmazonAppStore = getIsAmazonAppStore(appStoreType);
        const storeText = displayConsoleNameForAppStoreType(appStoreType);

        if (isAmazonAppStore) {
            labelText = isRecent ? 'Recent builds' : `${storeText} builds`;
            tooltipText =
                isRecent ?
                    `Recent builds available for upload to the ${storeText}`
                :   `Version codes associated with the Upcoming Version edit in the ${storeText}`;
        } else {
            tooltipText =
                isRecent ?
                    `Version codes higher than that of the last release in ${storeText}`
                :   `Version codes equal to or less than that of the last release in ${storeText}`;
        }
    }

    return (
        <Space align="center">
            <Text>{labelText}</Text>
            {tooltipText && (
                <Tooltip title={tooltipText}>
                    <QuestionCircleOutlined style={{ color: antGrey.grey6 }} />
                </Tooltip>
            )}
        </Space>
    );
};
